import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import HeadteacherDashboard from './HeadteacherDashboard';
import TeacherDashboard from './TeacherDashboard';
import { 
  Users, 
  BookOpen, 
  ArrowLeftRight,
  GraduationCap,
  Loader2
} from 'lucide-react';

const DashboardSwitcher = () => {
  const { user } = useAuth();
  const [currentDashboard, setCurrentDashboard] = useState('headteacher');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Auto-set dashboard based on user role
    if (user?.role === 'headteacher') {
      setCurrentDashboard('headteacher');
    } else if (user?.role === 'teacher') {
      setCurrentDashboard('teacher');
    }
    setLoading(false);
  }, [user]);

  // Only show for headteacher role (they can switch to teacher view)
  // Teachers get direct teacher dashboard without switching option
  if (user?.role === 'teacher') {
    return <TeacherDashboard />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-blue-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Loading Dashboard...</h2>
        </div>
      </div>
    );
  }

  if (user?.role !== 'headteacher') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-red-600">Access Denied</CardTitle>
            <CardDescription className="text-center">
              You don't have permission to access this dashboard.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Dashboard Switch Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <ArrowLeftRight className="h-4 w-4" />
              <span>Switch View:</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant={currentDashboard === 'headteacher' ? 'default' : 'outline'}
              onClick={() => setCurrentDashboard('headteacher')}
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              Headteacher View
            </Button>
            <Button
              variant={currentDashboard === 'teacher' ? 'default' : 'outline'}
              onClick={() => setCurrentDashboard('teacher')}
              className="flex items-center gap-2"
            >
              <BookOpen className="h-4 w-4" />
              Teacher View
            </Button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="transition-all duration-300 ease-in-out">
        {currentDashboard === 'headteacher' ? (
          <HeadteacherDashboard />
        ) : (
          <TeacherDashboard />
        )}
      </div>
    </div>
  );
};

export default DashboardSwitcher;